import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, MessageSquare, MapPin, ArrowDown } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1552519507-da3b142c6e3d?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";

const lines = [
  { text: "PRECISION", accent: false },
  { text: "CAR DETAILING", accent: true },
  { text: "ON YOUR DRIVEWAY", accent: false },
];

const lineVariant = {
  hidden: { y: "115%" },
  visible: (i) => ({
    y: "0%",
    transition: { delay: 0.35 + i * 0.16, duration: 0.9, ease: [0.16, 1, 0.3, 1] },
  }),
};

export default function Hero({ onBook }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "45%"]);

  const onMouseMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty("--mouse-x", `${e.clientX - r.left}px`);
    e.currentTarget.style.setProperty("--mouse-y", `${e.clientY - r.top}px`);
  };

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      onMouseMove={onMouseMove}
      className="relative min-h-screen flex flex-col justify-end overflow-hidden"
    >
      <motion.div style={{ y: imgY }} className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt="Luxury car being detailed"
          className="w-full h-[120%] object-cover opacity-55"
        />
        <div className="absolute inset-0 bg-[#0B0D10]/45" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0D10] via-transparent to-[#0B0D10]/70" />
      </motion.div>
      <div className="absolute inset-0 spotlight pointer-events-none" />
      <div className="absolute inset-0 grain-overlay opacity-60 pointer-events-none" />

      <motion.div style={{ y: textY }} className="relative max-w-7xl mx-auto w-full px-5 sm:px-8 pb-16 sm:pb-24 pt-40">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.7 }}
          className="flex items-center gap-3 mb-8"
          data-testid="hero-badge"
        >
          <span className="h-2 w-2 rounded-full bg-amber-400 animate-pulse" />
          <span className="font-mono text-[11px] sm:text-xs uppercase tracking-[0.3em] text-amber-400">
            Mobile Service — We Come To You
          </span>
        </motion.div>

        <h1
          data-testid="hero-headline"
          className="font-syne font-extrabold uppercase tracking-tight leading-[0.93] text-[13vw] sm:text-7xl lg:text-8xl text-white"
        >
          {lines.map((l, i) => (
            <span key={l.text} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${l.accent ? "text-amber-400" : ""}`}
                variants={lineVariant}
                custom={i}
                initial="hidden"
                animate="visible"
              >
                {l.text}
              </motion.span>
            </span>
          ))}
        </h1>

        <div className="mt-10 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-10">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.0, duration: 0.8 }}
            data-testid="hero-subtext"
            className="max-w-md text-slate-300 text-base sm:text-lg leading-relaxed"
          >
            P&H Mobile Detailing brings the showroom shine to your home or office.
            From a maintenance wash to full-blown disaster recovery — one text and
            we handle the rest.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.15, duration: 0.8 }}
            className="flex flex-col sm:flex-row gap-4"
          >
            <a
              href="tel:9197373526"
              data-testid="hero-call-button"
              className="group inline-flex items-center justify-center gap-3 rounded-full bg-amber-500 hover:bg-amber-400 text-black font-bold px-8 py-4 text-base transition-colors duration-300"
            >
              <Phone size={18} strokeWidth={2.5} className="group-hover:rotate-12 transition-transform duration-300" />
              Call 919-737-3526
            </a>
            <button
              onClick={onBook}
              data-testid="hero-book-button"
              className="group inline-flex items-center justify-center gap-3 rounded-full border border-white/20 hover:border-amber-400/60 hover:bg-white/5 text-white font-semibold px-8 py-4 text-base transition-[border-color,background-color] duration-300"
            >
              <MessageSquare size={18} className="text-amber-400" />
              Text To Book
            </button>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 1 }}
          className="mt-14 pt-8 border-t border-white/10 flex flex-wrap gap-x-10 gap-y-4 items-center"
          data-testid="hero-meta"
        >
          <span className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-slate-400">
            <MapPin size={14} className="text-amber-400" /> Benson, Clayton & Surrounding Areas
          </span>
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-slate-400">
            3 Packages — $60 to $230
          </span>
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-slate-400 hidden sm:inline">
            Pet Hair & Stain Specialists
          </span>
          <span className="ml-auto hidden lg:inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-slate-500">
            Scroll <ArrowDown size={14} className="animate-bounce" />
          </span>
        </motion.div>
      </motion.div>
    </section>
  );
}
